/* ==========================================================================
   Page – Demande de rétractation
   - Soumission AJAX vers l'API Willemse (POST JSON)
   - Validation HTML5 + longueurs conformes à la doc API
   - Gestion des codes retour 200/400/404/422/500 avec messages UX distincts
   - Traductions injectées via window.retractationI18n (voir bloc <script> dans la section liquid)
   - Aucune pollution globale (IIFE)
   ========================================================================== */

(function () {
  'use strict';

  const sections = document.querySelectorAll('[data-section-type="page-retractation"]');
  if (!sections.length) return;

  sections.forEach(initSection);

  function initSection(section) {
    const form = section.querySelector('.retractation__form');
    if (!form) return;

    const sectionId = section.getAttribute('data-section-id');
    const i18n = (window.retractationI18n && window.retractationI18n[sectionId]) || {};

    initDateField(section);
    initOrderUppercase(section);
    initInlineValidationReset(form);
    prefillFromQuery(section);
    bindSubmit(form, i18n);
  }

  /**
   * Helper de traduction : retourne la clé i18n si présente, sinon le fallback.
   */
  function tr(i18n, key, fallback) {
    return (i18n && typeof i18n[key] === 'string' && i18n[key].length) ? i18n[key] : fallback;
  }

  /* ---------- Date : borne à aujourd'hui max, il y a 60 jours min ---------- */
  function initDateField(section) {
    const dateInput = section.querySelector('#retractation-date');
    if (!dateInput) return;
    const today = new Date();
    const iso = (d) => d.toISOString().slice(0, 10);
    dateInput.max = iso(today);
    const min = new Date(today);
    min.setDate(min.getDate() - 60);
    dateInput.min = iso(min);
  }

  /* ---------- Numéro de commande : uppercase automatique ---------- */
  function initOrderUppercase(section) {
    const input = section.querySelector('#retractation-order');
    if (!input) return;
    input.addEventListener('input', () => {
      const start = input.selectionStart;
      const end = input.selectionEnd;
      input.value = input.value.toUpperCase();
      try { input.setSelectionRange(start, end); } catch (e) { /* noop */ }
    });
  }

  /* ---------- Retire l'état d'erreur dès que le champ redevient valide ---------- */
  function initInlineValidationReset(form) {
    form.addEventListener('input', (e) => {
      const el = e.target;
      if (el && el.willValidate && el.checkValidity()) {
        el.removeAttribute('aria-invalid');
      }
    });
  }

  /* ---------- Pré-remplissage via ?order=...&email=... ---------- */
  function prefillFromQuery(section) {
    if (typeof URLSearchParams === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const map = {
      order:     '#retractation-order',
      email:     '#retractation-email',
      firstname: '#retractation-firstname',
      lastname:  '#retractation-lastname',
      product:   '#retractation-product',
      phone:     '#retractation-phone'
    };
    Object.keys(map).forEach((key) => {
      const val = params.get(key);
      if (!val) return;
      const el = section.querySelector(map[key]);
      if (el && !el.value) el.value = val;
    });
  }

  /* ---------- Submit AJAX ---------- */
  function bindSubmit(form, i18n) {
    form.addEventListener('submit', async (event) => {
      event.preventDefault();

      hideAlerts(form);

      if (!validateForm(form)) return;

      const endpoint = (form.dataset.endpoint || '').trim();
      if (!endpoint) {
        showError(
          form,
          tr(i18n, 'endpoint_missing_title', 'Service temporairement indisponible.'),
          tr(i18n, 'endpoint_missing_body', 'La configuration de l\'endpoint est manquante. Merci de contacter le service client.')
        );
        return;
      }

      const payload = buildPayload(form, i18n);
      if (payload.motif && payload.motif.length > 1550) {
        payload.motif = payload.motif.slice(0, 1547) + '...';
      }

      setSubmitting(form, true, i18n);

      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify(payload)
        });

        await handleResponse(response, form, i18n);
      } catch (err) {
        console.error('[retractation] network error', err);
        showError(
          form,
          tr(i18n, 'network_title', 'Impossible de contacter le service.'),
          tr(i18n, 'network_body', 'Vérifiez votre connexion internet et réessayez. Si le problème persiste, contactez notre service client.')
        );
      } finally {
        setSubmitting(form, false, i18n);
      }
    });
  }

  /* ---------- Validation front (HTML5 + focus 1er invalide) ---------- */
  function validateForm(form) {
    let firstInvalid = null;
    Array.from(form.elements).forEach((el) => {
      if (!el.willValidate) return;
      if (!el.checkValidity()) {
        el.setAttribute('aria-invalid', 'true');
        if (!firstInvalid) firstInvalid = el;
      } else {
        el.removeAttribute('aria-invalid');
      }
    });
    if (firstInvalid) {
      firstInvalid.focus({ preventScroll: false });
      firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }
    return true;
  }

  /* ---------- Construction du body JSON conforme au contrat API ---------- */
  function buildPayload(form, i18n) {
    const val = (name) => {
      const el = form.elements.namedItem(name);
      return el && typeof el.value === 'string' ? el.value.trim() : '';
    };

    const payload = {
      reference: val('reference'),
      nom:       val('nom'),
      prenom:    val('prenom'),
      email:     val('email')
    };

    const telephone = val('telephone');
    if (telephone) payload.telephone = telephone;

    const motif = buildMotif(val, i18n);
    if (motif) payload.motif = motif;

    return payload;
  }

  /* ---------- Agrégation UX (produit, date, motif, message) dans le champ `motif` API ---------- */
  function buildMotif(val, i18n) {
    const parts = [];
    const produit    = val('produit');
    const date       = val('date_reception');
    const motifType  = val('motif_type');
    const message    = val('message');

    if (produit)   parts.push(tr(i18n, 'motif_produit', 'Produit(s) concerné(s) : ') + produit);
    if (date)      parts.push(tr(i18n, 'motif_date',    'Date de réception du colis : ') + formatDateFR(date));
    if (motifType) parts.push(tr(i18n, 'motif_type',    'Motif sélectionné : ') + motifType);
    if (message)   parts.push(tr(i18n, 'motif_message', 'Message libre : ') + message);

    return parts.join('\n');
  }

  function formatDateFR(iso) {
    if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
    const [y, m, d] = iso.split('-');
    return `${d}/${m}/${y}`;
  }

  /* ---------- Dispatcher des réponses API ---------- */
  async function handleResponse(response, form, i18n) {
    let body = null;
    const contentType = response.headers.get('content-type') || '';
    try {
      body = contentType.includes('application/json') ? await response.json() : await response.text();
    } catch (e) {
      body = null;
    }

    switch (response.status) {
      case 200:
      case 201: {
        const id = typeof body === 'number' ? body : (body && body.id) || null;
        const bodyTemplate = tr(i18n, 'success_body', 'Un accusé de réception vous sera envoyé par email. Notre service client vous recontactera prochainement.');
        const refPrefix = tr(i18n, 'success_ref_prefix', 'Votre numéro de référence est');
        const idHtml = id ? ` ${refPrefix} <strong>#${id}</strong>.` : '';
        showSuccess(
          form,
          tr(i18n, 'success_title', 'Demande enregistrée.'),
          `${bodyTemplate}${idHtml}`
        );
        form.reset();
        return;
      }

      case 400:
        showError(
          form,
          tr(i18n, 's400_title', 'Demande déjà enregistrée.'),
          tr(i18n, 's400_body', 'Une demande de rétractation existe déjà pour cette commande. Notre service client va vous recontacter.')
        );
        return;

      case 404:
        showError(
          form,
          tr(i18n, 's404_title', 'Commande introuvable.'),
          tr(i18n, 's404_body', 'Aucune commande ne correspond au numéro et à l\'email indiqués. Vérifiez que vous utilisez bien l\'email associé à votre commande.')
        );
        highlightField(form, 'reference');
        highlightField(form, 'email');
        return;

      case 422: {
        const details = extractValidationDetails(body);
        showError(
          form,
          tr(i18n, 's422_title', 'Certaines informations sont invalides.'),
          details || tr(i18n, 's422_body', 'Merci de vérifier les champs marqués et de réessayer.')
        );
        return;
      }

      case 401:
        showError(
          form,
          tr(i18n, 's401_title', 'Erreur d\'autorisation.'),
          tr(i18n, 's401_body', 'L\'accès au service de rétractation a échoué. Merci de contacter notre service client.')
        );
        return;

      case 500:
      case 502:
      case 503:
      case 504:
        showError(
          form,
          tr(i18n, 's5xx_title', 'Service temporairement indisponible.'),
          tr(i18n, 's5xx_body', 'Une erreur technique est survenue. Veuillez réessayer dans quelques minutes ou contacter notre service client.')
        );
        return;

      default: {
        const prefix = tr(i18n, 'default_body_prefix', 'Le service a répondu avec le code');
        const suffix = tr(i18n, 'default_body_suffix', '. Merci de réessayer ou de contacter notre service client.');
        showError(
          form,
          tr(i18n, 'default_title', 'Erreur inattendue.'),
          `${prefix} ${response.status}${suffix}`
        );
      }
    }
  }

  function extractValidationDetails(body) {
    if (!body || typeof body !== 'object') return '';
    if (Array.isArray(body.details) && body.details.length) {
      return body.details.map((d) => d.message || d).join(' · ');
    }
    if (body.message) return body.message;
    return '';
  }

  function highlightField(form, name) {
    const el = form.elements.namedItem(name);
    if (el) el.setAttribute('aria-invalid', 'true');
  }

  /* ---------- État bouton pendant l'envoi ---------- */
  function setSubmitting(form, isSubmitting, i18n) {
    const btn = form.querySelector('[data-submit-btn]');
    if (!btn) return;
    const iconDefault = btn.querySelector('.retractation__btn-icon-default');
    const iconSpinner = btn.querySelector('.retractation__btn-spinner');
    const label = btn.querySelector('[data-submit-label]');

    btn.disabled = isSubmitting;
    btn.setAttribute('aria-busy', isSubmitting ? 'true' : 'false');
    if (iconDefault) iconDefault.hidden = isSubmitting;
    if (iconSpinner) iconSpinner.hidden = !isSubmitting;
    if (label) {
      label.textContent = isSubmitting
        ? tr(i18n, 'submit_loading', 'Envoi en cours…')
        : tr(i18n, 'submit_default', 'Annuler ou retourner ma commande');
    }
  }

  /* ---------- Affichage alertes ---------- */
  function hideAlerts(form) {
    form.querySelectorAll('[data-alert]').forEach((el) => { el.hidden = true; });
  }

  function showSuccess(form, title, htmlMessage) {
    const alert = form.querySelector('[data-alert="success"]');
    if (!alert) return;
    const titleEl = alert.querySelector('strong');
    const msgEl = alert.querySelector('[data-alert-message]');
    if (titleEl) titleEl.textContent = title;
    if (msgEl) msgEl.innerHTML = htmlMessage;
    alert.hidden = false;
    scrollToAlert(alert);
  }

  function showError(form, title, message) {
    const alert = form.querySelector('[data-alert="error"]');
    if (!alert) return;
    const titleEl = alert.querySelector('[data-alert-title]');
    const msgEl = alert.querySelector('[data-alert-message]');
    if (titleEl) titleEl.textContent = title;
    if (msgEl) msgEl.textContent = message;
    alert.hidden = false;
    scrollToAlert(alert);
  }

  function scrollToAlert(alert) {
    requestAnimationFrame(() => {
      alert.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }

})();
